<script>
import utilsMixin from '@shared/mixins/utils';

export default {
  mixins: [utilsMixin],
  props: {
    membership: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      pendingAction: null,
    };
  },
  computed: {
    membershipItems() {
      return (this.membership.items || []).filter(Boolean);
    },
    visibleItems() {
      return this.membershipItems.slice(0, 8);
    },
    remainingItemsCount() {
      const remaining = this.membershipItems.length - this.visibleItems.length;
      return remaining > 0 ? remaining : 0;
    },
  },
  watch: {
    membership: {
      handler() {
        this.resetPendingAction();
      },
      deep: true,
    },
  },
  methods: {
    hasDiscountPrice(price) {
      if (!price) {
        return false;
      }

      const amount = Number(price.amount || 0);
      const discounted = price.discount_price;

      if (discounted == null) {
        return false;
      }

      return Number(discounted) < amount;
    },
    getEffectivePrice(price) {
      if (!price) {
        return 0;
      }

      const target = this.hasDiscountPrice(price) ? price.discount_price : price.amount;
      return Number(target || 0);
    },
    getItemLabel(item) {
      if (item && item.buyable && item.buyable.name) {
        return item.buyable.name;
      }
      return this.$t('content_removed');
    },
    createActionControls(action) {
      const trackLoading = action !== 'cancel';

      return {
        start: () => {
          if (trackLoading && !this.pendingAction) {
            this.pendingAction = action;
          }
        },
        stop: () => {
          if (trackLoading && this.pendingAction === action) {
            this.resetPendingAction();
          }
        },
      };
    },
    resetPendingAction() {
      this.pendingAction = null;
    },
    getStatusLabel(membership) {
      const { status } = membership;
      if (status === 'active') {
        return this.$t('status.active');
      } if (status === 'cancel_at_period_end') {
        return this.$t('status.cancel_at_period_end', { date: this.formatDate(membership.current_period_end) });
      } if (status === 'canceled') {
        return this.$t('status.canceled');
      } if (status === 'expired') {
        return this.$t('status.expired');
      } if (status === 'paused') {
        return this.$t('status.paused');
      }
      return status;
    },
    getStatusType(membership) {
      if (membership.status === 'active') {
        return 'is-success';
      } if (membership.status === 'cancel_at_period_end') {
        return 'is-warning';
      } if (membership.status === 'paused') {
        return 'is-warning';
      }
      return 'is-danger';
    },
    formatDate(date) {
      return this.$moment(date).format('DD/MM/YYYY');
    },
    getFrequencyLabelByInterval(buyable) {
      const lang = this.$i18n?.locale || 'fr';
      return this.formatIntervalShort(buyable, lang).toLowerCase();
    },
    cancelMembership() {
      if (this.pendingAction) {
        return;
      }

      this.$emit('cancel', this.membership, this.createActionControls('cancel'));
    },
    reactivateMembership() {
      if (this.pendingAction) {
        return;
      }

      this.$emit('reactivate', this.membership, this.createActionControls('reactivate'));
    },
    rejoindMembership() {
      if (this.pendingAction) {
        return;
      }

      this.$emit('rejoin', this.membership, this.createActionControls('rejoin'));
    },
  },
  i18n: {
    messages: {
      fr: {
        status: {
          active: 'Actif',
          cancel_at_period_end: 'Se termine le {date}',
          canceled: 'Annulé',
          expired: 'Expiré',
          paused: 'En pause',
        },
        paused_message: 'Votre abonnement est en pause. Contactez le formateur pour plus d\'informations.',
        ends_on: 'Fin le {date}',
        next_billing: 'Prochaine facturation',
        actions: {
          reactivate: 'Réactiver',
          cancel: 'Annuler',
          rejoin: 'Reprendre l\'abonnement',
        },
        content_included_title: 'Contenu inclus :',
        content_removed: 'Contenu supprimé',
        additional_items: '+{count} autre|+{count} autres',
      },
      en: {
        status: {
          active: 'Active',
          cancel_at_period_end: 'Ends on {date}',
          canceled: 'Cancelled',
          expired: 'Expired',
          paused: 'Paused',
        },
        paused_message: 'Your membership is paused. Contact the trainer for more information.',
        ends_on: 'Ends on {date}',
        next_billing: 'Next billing',
        actions: {
          reactivate: 'Reactivate',
          cancel: 'Cancel',
          rejoin: 'Resume subscription',
        },
        content_included_title: 'Included content:',
        content_removed: 'Content removed',
        additional_items: '+{count} other|+{count} others',
      },
    },
  },
};
</script>

<template>
  <div class="card mb-4">
    <div class="card-content">
      <div class="media">
        <div class="media-content">
          <h3 class="is-6 mb-1">
            {{ membership.membership.name }}
            <b-tag :type="getStatusType(membership)" class="ml-2">
              {{ getStatusLabel(membership) }}
            </b-tag>
          </h3>
          <p class="is-7 mb-2" v-if="membership.price">
            <span>
              <template v-if="hasDiscountPrice(membership.price)">
                <strong>
                  {{ getEffectivePrice(membership.price) | formatPrice }}
                </strong>
                <span class="has-text-grey-dark ml-2" style="text-decoration: line-through;">
                  {{ membership.price.amount | formatPrice }}
                </span>
              </template>
              <template v-else>
                {{ membership.price.amount | formatPrice }}
              </template>
            </span>
            / {{ getFrequencyLabelByInterval(membership.price) }}
            <span v-if="membership.canceled_at && membership.current_period_end">
              • {{ $t('ends_on', { date: formatDate(membership.current_period_end) }) }}
            </span>
          </p>
          <p v-if="membership.status === 'active' || membership.status === 'cancel_at_period_end'" class="is-size-7">
            {{ $t('next_billing') }} : {{ formatDate(membership.current_period_end) }}
          </p>
          <p v-if="membership.status === 'paused'" class="is-size-7 has-text-warning-dark">
            {{ $t('paused_message') }}
          </p>
        </div>
        <div class="media-right">
          <div class="buttons">
            <b-button v-if="membership.status === 'cancel_at_period_end'" type="is-success" size="is-small"
              :loading="pendingAction === 'reactivate'" :disabled="!!pendingAction" @click="reactivateMembership">
              {{ $t('actions.reactivate') }}
            </b-button>
            <b-button v-else-if="membership.status === 'active'" type="is-warning" size="is-small"
              :loading="pendingAction === 'cancel'" :disabled="!!pendingAction" @click="cancelMembership">
              {{ $t('actions.cancel') }}
            </b-button>
            <b-button v-else-if="membership.status !== 'paused'" type="is-success" size="is-small" :loading="pendingAction === 'rejoin'"
              :disabled="!!pendingAction" @click="rejoindMembership">
              {{ $t('actions.rejoin') }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Content included -->
      <div v-if="membershipItems.length" class="mt-4">
        <p class="has-text-weight-semibold is-size-7 mb-2">{{ $t('content_included_title') }}</p>
        <div class="tags">
          <b-tag v-for="item in visibleItems" :key="item.id || item.uuid" type="is-light" class="mr-1 mb-1">
            {{ getItemLabel(item) }}
          </b-tag>
          <b-tag v-if="remainingItemsCount" type="is-light">
            {{ $tc('additional_items', remainingItemsCount, { count: remainingItemsCount }) }}
          </b-tag>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  transition: box-shadow 0.2s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>
